import {Injectable} from "@angular/core";

import {ApiService} from "app/services/domain/api.service";
import * as SharedSvc from "app/services/domain/base.service";
import {WafAssessmentDefinitionsService} from "app/services/domain/waf-assessment-definitions.service";
import * as Models from "app/services/proxy/model/models";

import {CacheService} from "framework/services/cache.service";
import {ErrorService} from "framework/services/error.service";
import {Lookup} from "framework/services/utils.service";

@Injectable()
export class WafAssessmentVersionsService extends SharedSvc.BaseDefinitionVersionService<Models.WellArchitectedFrameworkAssessmentConfiguration, Models.WellArchitectedFrameworkAssessmentDefinitionVersion, Models.WellArchitectedFrameworkAssessmentDefinition, WafAssessmentDefinitionVersionExtended>
{
    constructor(api: ApiService,
                errors: ErrorService,
                cache: CacheService)
    {
        super(api, errors, cache, Models.WellArchitectedFrameworkAssessmentDefinitionVersion, WafAssessmentDefinitionVersionExtended.newInstance);
    }

    //--//

    protected cachePrefix(): string { return "WafAssessmentDefinitionVersion"; }

    protected getRaw(id: string): Promise<Models.WellArchitectedFrameworkAssessmentDefinitionVersion>
    {
        return this.api.wafAssessmentDefinitionVersionsApi.get(id);
    }

    protected getBatchRaw(ids: string[]): Promise<Models.WellArchitectedFrameworkAssessmentDefinitionVersion[]>
    {
        const request = Models.RecordIdentityBatchRequest.newInstance({ids: ids.map(id => WafAssessmentDefinitionVersionExtended.newIdentity(id))});
        return this.api.wafAssessmentDefinitionVersionsApi.batch(request);
    }

    protected canBatch(): boolean
    {
        return true;
    }

    createVersion(model: Models.WellArchitectedFrameworkAssessmentDefinitionVersion): Promise<Models.WellArchitectedFrameworkAssessmentDefinitionVersion>
    {
        return this.api.wafAssessmentDefinitionVersionsApi.create(model);
    }

    async makeHead(id: string): Promise<WafAssessmentDefinitionVersionExtended>
    {
        let model = await this.api.wafAssessmentDefinitionVersionsApi.makeHead(id);
        return this.wrapModel(model);
    }

    async makeRelease(id: string): Promise<WafAssessmentDefinitionVersionExtended>
    {
        let model = await this.api.wafAssessmentDefinitionVersionsApi.makeRelease(id);
        return this.wrapModel(model);
    }

    async setVersionState(versionId: string,
                          state: Models.VersionState)
    {
        await this.api.wafAssessmentDefinitionVersionsApi.setState(versionId, state);
    }
}

export class WafAssessmentDefinitionVersionExtended extends SharedSvc.ExtendedApprovableDefinitionVersionModel<Models.WellArchitectedFrameworkAssessmentConfiguration, Models.WellArchitectedFrameworkAssessmentDefinitionVersion, Models.WellArchitectedFrameworkAssessmentDefinition>
{
    static newInstance(svc: WafAssessmentVersionsService,
                       model: Models.WellArchitectedFrameworkAssessmentDefinitionVersion): WafAssessmentDefinitionVersionExtended
    {
        return new WafAssessmentDefinitionVersionExtended(svc, model, "WafAssessmentDefinitionVersion");
    }

    public static newIdentity(sysId?: string): Models.RecordIdentity
    {
        return Models.RecordIdentity.newInstance({
                                                     table: "WafAssessmentDefinitionVersion",
                                                     sysId: sysId
                                                 });
    }

    //--//

    get hasMultipleFrameworks(): boolean
    {
        if (!this.model.details)
        {
            this.getDetails();
            return null;
        }

        return this.model.details.framework_ids?.length > 1;
    }

    protected definitionSvc(): WafAssessmentDefinitionsService
    {
        return this.domain.wafAssessmentDefinitions;
    }

    protected definitionVersionSvc(): WafAssessmentVersionsService
    {
        return this.domain.wafAssessmentVersions;
    }

    async approve()
    {
        return this.domain.wafAssessmentVersions.setVersionState(this.model.sysId, Models.VersionState.Approved);
    }
}

export class WellArchitectedAssessmentConfigurationExtended
{
    readonly syncProviders = [Models.CloudServiceName.AWS];

    constructor(public readonly frameworks: Lookup<Models.WellArchitectedFramework>,
                public readonly model: Models.WellArchitectedFrameworkAssessmentConfiguration)
    {}

    public get hasSyncableWorkload(): boolean
    {
        const selectedFrameworks = this.model.framework_ids.map((id) => this.frameworks[id]);
        return selectedFrameworks.some(f => !!f?.hyperscaler_framework_id);
    }

    public set syncEnabled(sync: boolean)
    {
        this.model.sync_with_hyperscaler = sync ? Models.WellArchitectedFrameworkAssessmentSync.newInstance({AWS: Models.WellArchitectedFrameworkAssessmentAwsSyncFields.newInstance({})}) : null;
    }

    public get syncEnabled(): boolean
    {
        return !!this.model.sync_with_hyperscaler;
    }

    public get syncAccount(): string
    {
        return this.model.sync_with_hyperscaler?.AWS?.cloud_account_id;
    }

    public set syncAccount(account: string)
    {
        if (this.syncEnabled)
        {
            const aws            = this.model.sync_with_hyperscaler.AWS;
            aws.cloud_account_id = account;
            aws.region           = null;
            aws.partner_id       = null;
        }
    }

    public get syncRegion(): string
    {
        return this.model.sync_with_hyperscaler?.AWS?.region;
    }

    public set syncRegion(region: string)
    {
        if (this.syncEnabled)
        {
            this.model.sync_with_hyperscaler.AWS.region = region;
        }
    }

    public get partnerId(): string
    {
        return this.model.sync_with_hyperscaler?.AWS?.partner_id;
    }

    public set partnerId(partnerId: string)
    {
        if (this.syncEnabled)
        {
            this.model.sync_with_hyperscaler.AWS.partner_id = partnerId;
        }
    }
}
